/* The kit's tooltip (the founder, 2026-10-12: "a CSS tooltip, not the browser's title"). Any element with `data-tip="…"` shows that text
 * in a small dark bubble on hover and on keyboard focus; the look and the fade are pure CSS (`styles/tip.css`). The knowledge sources round
 * (2026-10-22, bug (a): "the kit's CSS tooltip must flip below its anchor when there is no room above, and never be clipped by a table's
 * overflow"): the bubble is fixed to the window, so no table card or scrolling box cuts it, and this helper places it there: above its
 * element, centred and kept inside the window, below it when there is no room above. It measures the text once per hover or focus. */

export interface Box { top: number; right: number; bottom: number; left: number; }
export interface TipPlace { /** the bubble's left edge in the window (px) */ x: number; /** its top edge in the window (px) */ y: number; /** the widest the bubble may be (px) */ max: number; below: boolean; }

const GAP = 6;   // between the element and the bubble
const EDGE = 4;  // the least room between the bubble and the window's edge
const WIDEST = 256; // 16rem at the browser's 16 px

/** Pure: where a bubble of `size` sits for an element at `el` in a window `win`: centred on the element but shifted to stay inside, above it
 *  unless there is no room above (then below, whatever the room there: the window scrolls, a cut-off top does not). */
export function tipPlace(el: Box, win: Box, size: { width: number; height: number }): TipPlace {
  const max = Math.max(0, Math.min(WIDEST, win.right - win.left - 2 * EDGE));
  const w = Math.min(size.width, max);
  const centred = (el.left + el.right) / 2 - w / 2;
  const x = Math.max(win.left + EDGE, Math.min(centred, win.right - EDGE - w));
  const need = size.height + GAP + EDGE;
  const below = el.top - win.top < need && win.bottom - el.bottom >= el.top - win.top;
  return { x: Math.round(x), y: Math.round(below ? el.bottom + GAP : el.top - GAP - size.height), max, below };
}

/** the box a fixed bubble is placed in: the window, unless an ancestor holds fixed boxes itself (a transform, a filter, containment), whose
 *  corner then counts as the origin */
function fixedOrigin(el: Element): { left: number; top: number } {
  for (let p = el.parentElement; p && p !== document.documentElement; p = p.parentElement) {
    const s = getComputedStyle(p);
    if (s.transform !== "none" || s.filter !== "none" || /paint|layout|strict|content/.test(s.contain) || /transform|filter/.test(s.willChange)) {
      const r = p.getBoundingClientRect();
      return { left: r.left + p.clientLeft, top: r.top + p.clientTop };
    }
  }
  return { left: 0, top: 0 };
}

/** the bubble's size for this text at this widest, measured with the bubble's own class off screen */
function measure(text: string, max: number): { width: number; height: number } {
  const probe = document.createElement("span");
  probe.className = "ob-tip-probe";
  probe.style.maxWidth = `${max}px`;
  probe.textContent = text;
  document.body.appendChild(probe);
  const r = probe.getBoundingClientRect();
  probe.remove();
  return { width: r.width, height: r.height };
}

/** Sets the bubble's place on one `[data-tip]` element (CSS variables and `data-tip-place`). */
export function placeTip(el: HTMLElement) {
  const text = el.dataset.tip; if (!text) return;
  const win: Box = { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
  const max = Math.max(0, Math.min(WIDEST, win.right - win.left - 2 * EDGE));
  const at = tipPlace(el.getBoundingClientRect(), win, measure(text, max));
  const origin = fixedOrigin(el);
  el.style.setProperty("--ob-tip-left", `${at.x - origin.left}px`);
  el.style.setProperty("--ob-tip-top", `${at.y - origin.top}px`);
  el.style.setProperty("--ob-tip-max", `${at.max}px`);
  if (at.below) el.dataset.tipPlace = "below"; else delete el.dataset.tipPlace;
}

/** Once per app: places a bubble whenever a pointer enters or the focus reaches a `[data-tip]` element. Returns the way to stop. */
export function installTips(root: Document = document): () => void {
  const on = (e: Event) => { const el = (e.target as Element | null)?.closest?.("[data-tip]"); if (el instanceof HTMLElement) placeTip(el); };
  root.addEventListener("pointerover", on, true);
  root.addEventListener("focusin", on, true);
  return () => { root.removeEventListener("pointerover", on, true); root.removeEventListener("focusin", on, true); };
}
